import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { createGlobalStyle } from 'styled-components';

import { palette } from './primitives/palette';
import { fontFaces } from './styles/fontFaces';

export const GlobalStyles = createGlobalStyle`
  /* font face definitions */
  ${fontFaces};

  /* resets */
  body {
    margin: 0;
    padding: 0;
  }

  /* theme CSS variables */
  :root {
    ${themeVariables.borderRadius.full.desktop}: 9999px;
    ${themeVariables.borderRadius.full.mobile}: 9999px;
    ${themeVariables.borderRadius.md.desktop}: 8px;
    ${themeVariables.borderRadius.md.mobile}: 6px;

    ${themeVariables.colors.content.action}: ${palette.neutralOffWhite};
    ${themeVariables.colors.content.brand}: ${palette.purpleBase};
    ${themeVariables.colors.content.default}: ${palette.neutralBrandBlack};
    ${themeVariables.colors.content.disabled}: color-mix(in srgb, ${palette.neutralBrandBlack} 30%, var(${themeVariables.colors.page.default}));
    ${themeVariables.colors.content.interactive}: ${palette.deepPurpleBase};
    ${themeVariables.colors.content.link}: ${palette.blueBase};
    ${themeVariables.colors.content.negative}: ${palette.redBase};
    ${themeVariables.colors.content.positive}: ${palette.greenBase};
    ${themeVariables.colors.content.primary}: ${palette.neutralBrandBlack};
    ${themeVariables.colors.content.secondary}: color-mix(in srgb, ${palette.neutralBrandBlack} 70%, var(${themeVariables.colors.page.default}));
    ${themeVariables.colors.content.selected}: ${palette.purpleBase};
    ${themeVariables.colors.content.tertiary}: color-mix(in srgb, ${palette.neutralBrandBlack} 50%, var(${themeVariables.colors.page.default}));

    ${themeVariables.colors.overlay.default}: rgb(59 59 65 / 70%);
    ${themeVariables.colors.page.default}: ${palette.neutralOffWhite};

    ${themeVariables.colors.stroke.action}: ${palette.deepPurpleBase};
    ${themeVariables.colors.stroke.brand}: ${palette.deepPurpleBase};
    ${themeVariables.colors.stroke.default}: color-mix(in srgb, ${palette.neutralBrandBlack} 10%, var(${themeVariables.colors.page.default}));
    ${themeVariables.colors.stroke.focus}: color-mix(in srgb, ${palette.neutralBrandBlack} 50%, var(${themeVariables.colors.page.default}));
    ${themeVariables.colors.stroke.interactive}: color-mix(in srgb, ${palette.neutralBrandBlack} 30%, var(${themeVariables.colors.page.default}));
    ${themeVariables.colors.stroke.inverse}: ${palette.neutralOffWhite};
    ${themeVariables.colors.stroke.negative}: ${palette.redBase};
    ${themeVariables.colors.stroke.positive}: ${palette.greenBase};
    ${themeVariables.colors.stroke.selected}: ${palette.deepPurpleBase};

    ${themeVariables.colors.surface.action}: ${palette.purpleBase};
    ${themeVariables.colors.surface.brand}: color-mix(in srgb, ${palette.purpleBase} 10%, var(${themeVariables.colors.page.default}));
    ${themeVariables.colors.surface.default}: ${palette.neutralWhite};
    ${themeVariables.colors.surface.disabled}: color-mix(in srgb, ${palette.neutralBrandBlack} 10%, var(${themeVariables.colors.page.default}));
    ${themeVariables.colors.surface.interactive}: ${palette.deepPurpleBase};
    ${themeVariables.colors.surface.negative}: color-mix(in srgb, ${palette.redBase} 10%, var(${themeVariables.colors.page.default}));
    ${themeVariables.colors.surface.notice}: color-mix(in srgb, ${palette.yellowBase} 10%, var(${themeVariables.colors.page.default}));
    ${themeVariables.colors.surface.positive}: color-mix(in srgb, ${palette.greenBase} 10%, var(${themeVariables.colors.page.default}));
    ${themeVariables.colors.surface.selected}: ${palette.purpleBase};

    ${themeVariables.spacing.xxs}: 4px;
    ${themeVariables.spacing.xs}: 8px;
    ${themeVariables.spacing.sm}: 12px;
    ${themeVariables.spacing.md}: 16px;
    ${themeVariables.spacing.lg}: 24px;
    ${themeVariables.spacing.xl}: 32px;
    ${themeVariables.spacing.xxl}: 40px;
    ${themeVariables.spacing.xxxl}: 80px;

    ${themeVariables.strokeWidth}: 1px;

    ${themeVariables.tint.disabled}: 30%;
    ${themeVariables.tint.hover}: 10%;

    ${themeVariables.typography.heading.fontFamily}: 'NOTHS Suisse Neue', 'Lato',
      Arial, sans-serif;
    ${themeVariables.typography.heading.fontWeight}: 500;
    ${themeVariables.typography.heading.letterSpacing}: -4%;

    ${themeVariables.typography.heading.sizes.xs.fontSize}: 18px;
    ${themeVariables.typography.heading.sizes.xs.lineHeight}: 24px;
    ${themeVariables.typography.heading.sizes.sm.fontSize}: 20px; 
    ${themeVariables.typography.heading.sizes.sm.lineHeight}: 28px;
    ${themeVariables.typography.heading.sizes.md.fontSize}: 24px;
    ${themeVariables.typography.heading.sizes.md.lineHeight}: 32px;
    ${themeVariables.typography.heading.sizes.lg.fontSize}: 30px;
    ${themeVariables.typography.heading.sizes.lg.lineHeight}: 38px;
    ${themeVariables.typography.heading.sizes.xl.fontSize}: 36px;
    ${themeVariables.typography.heading.sizes.xl.lineHeight}: 44px;
    ${themeVariables.typography.heading.sizes.xxl.fontSize}: 48px;
    ${themeVariables.typography.heading.sizes.xxl.lineHeight}: 56px;

    ${themeVariables.typography.body.fontFamily}: 'Lato', Arial, sans-serif;
    ${themeVariables.typography.body.fontWeight}: 400;
    ${themeVariables.typography.body.letterSpacing}: 1%;

    ${themeVariables.typography.body.sizes.sm.fontSize}: 13px;
    ${themeVariables.typography.body.sizes.sm.lineHeight}: 18px;
    ${themeVariables.typography.body.sizes.md.fontSize}: 15px;
    ${themeVariables.typography.body.sizes.md.lineHeight}: 20px;
    ${themeVariables.typography.body.sizes.lg.fontSize}: 17px;
    ${themeVariables.typography.body.sizes.lg.lineHeight}: 24px;
  }

  /* base styles */
  body {
    -webkit-font-smoothing: antialiased;
    font-family: var(${themeVariables.typography.body.fontFamily});
    font-weight: var(${themeVariables.typography.body.fontWeight});
    letter-spacing: var(${themeVariables.typography.body.letterSpacing});
    background-color: var(${themeVariables.colors.page.default});
  }

  *, *:before, *:after {
    box-sizing: border-box;
  }
  
  h1, h2, h3, h4, h5, h6 {
    font-family: var(${themeVariables.typography.heading.fontFamily});
    font-weight: var(${themeVariables.typography.heading.fontWeight});
    letter-spacing: var(${themeVariables.typography.heading.letterSpacing});
  }
  
  h1 {
    font-size: var(${themeVariables.typography.heading.sizes.xxl.fontSize});
    line-height: var(${themeVariables.typography.heading.sizes.xxl.lineHeight});
  }
  
  h2 {
    font-size: var(${themeVariables.typography.heading.sizes.xl.fontSize});
    line-height: var(${themeVariables.typography.heading.sizes.xl.lineHeight});
  }

  h3 {
    font-size: var(${themeVariables.typography.heading.sizes.lg.fontSize});
    line-height: var(${themeVariables.typography.heading.sizes.lg.lineHeight});
  }
  
  h4 {
    font-size: var(${themeVariables.typography.heading.sizes.md.fontSize});
    line-height: var(${themeVariables.typography.heading.sizes.md.lineHeight});
  }
  
  h5 {
    font-size: var(${themeVariables.typography.heading.sizes.sm.fontSize});
    line-height: var(${themeVariables.typography.heading.sizes.sm.lineHeight});
  }

  h6 {
    font-size: var(${themeVariables.typography.heading.sizes.xs.fontSize});
    line-height: var(${themeVariables.typography.heading.sizes.xs.lineHeight});
  }
  
  p {
    font-size: var(${themeVariables.typography.body.sizes.lg.fontSize});
    line-height: var(${themeVariables.typography.body.sizes.lg.lineHeight});
  }
  
  a {
    color: var(${themeVariables.colors.content.link});
  }
`;
